import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { langMeta, langPrefix, stripLangPrefix, type Lang, DEFAULT_LANG } from '@/i18n';

interface GeoRecommendBannerProps {
  recommendedLang: Lang;
  currentLang: Lang;
}

const GeoRecommendBanner = ({ recommendedLang, currentLang }: GeoRecommendBannerProps) => {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (currentLang !== DEFAULT_LANG || recommendedLang === currentLang) {
      setDismissed(true);
    }
  }, [currentLang, recommendedLang]);

  if (dismissed || recommendedLang === currentLang || currentLang !== DEFAULT_LANG) return null;

  const meta = langMeta[recommendedLang];

  const switchLang = () => {
    const basePath = stripLangPrefix(pathname);
    const newPath = `${langPrefix(recommendedLang)}${basePath}`;
    localStorage.setItem('pt_geo_lang', recommendedLang);
    setDismissed(true);
    navigate(`${newPath}${search}${hash}`, { replace: true });
  };

  const dismiss = () => {
    localStorage.setItem('pt_geo_lang', currentLang);
    setDismissed(true);
  };

  const messages: Record<string, string> = {
    ko: '한국어로 보시겠습니까?',
    ja: '日本語で表示しますか？',
    th: 'ต้องการดูเป็นภาษาไทยหรือไม่?',
    vi: 'Bạn muốn xem bằng Tiếng Việt?',
    id: 'Ingin melihat dalam Bahasa Indonesia?',
    ms: 'Ingin melihatnya dalam Bahasa Melayu?',
    'pt-BR': 'Quer ver em Português?',
    my: 'မြန်မာဘာသာဖြင့် ကြည့်ရှုလိုပါသလား။',
    en: 'View in English?',
    'zh-TW': '要以繁體中文顯示嗎？',
    'zh-CN': '要以简体中文显示吗？',
    de: 'Auf Deutsch anzeigen?',
    es: '¿Ver en Español?',
    fr: 'Afficher en Français ?',
    ar: 'هل تريد العرض بالعربية؟',
  };

  return (
    <div className="bg-accent/10 border-b border-accent/20 px-4 py-2.5 flex items-center justify-center gap-3 text-sm">
      <Globe className="w-4 h-4 text-accent shrink-0" />
      <span className="text-foreground">
        {meta.flag} {messages[recommendedLang] || `Switch to ${meta.name}?`}
      </span>
      <Button size="sm" variant="default" className="h-7 px-3 text-xs" onClick={switchLang}>
        {meta.name}
      </Button>
      <button onClick={dismiss} className="text-muted-foreground hover:text-foreground transition-colors p-1" aria-label="Dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default GeoRecommendBanner;
